import { AUTH_CONFIG } from './auth.config';
import { generateCodeVerifier, generateCodeChallenge } from './pkce';
import constants from "../../constants";
import request from "../request";
import { resolveSafeRedirect } from './safe-redirect';

const SESSION_EXPIRED_NOTIFY_KEY = '__sso_session_expired_notify';
/** Sau logout VNeID (redirect_uri về SSO login), quay lại app gọi logout (vd. QLST). */
const POST_LOGOUT_RETURN_KEY = '__sso_post_logout_return';

// Fallback tạo state khi crypto.randomUUID không khả dụng (HTTP / insecure context)
function randomState(): string {
    if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
        return (crypto as any).randomUUID();
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // UUID v4
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

interface TokenResponse {
    access_token: string;
    refresh_token: string;
    id_token: string;
    token_type: string;
    expires_in: number;
    scope: string;
}

interface AuthState {
    codeVerifier: string;
    state: string;
    returnUrl: string;
}

export interface DmdcCallbackResult {
    resultCode: 'LOGIN_SUCCESS' | 'LINK_REQUIRED' | 'LINK_COMPLETED';
    redirectUrl: string | null;
    linkToken?: string;
    linkUsername?: string;
}

class AuthService {
    private readonly AUTH_STATE_KEY = 'sso_auth_state';
    private readonly TOKENS_KEY = 'sso_tokens';
    private isRedirecting = false; // Flag để tránh redirect nhiều lần
    private readonly DEFAULT_AFTER_LOGIN_URL = `${constants.BASE_URL}/nguoi-dung`;

    /**
     * Bước 1: Khởi tạo luồng đăng nhập
     */
    async startLogin(returnUrl: string = '/'): Promise<void> {
        // Tránh redirect loop: nếu đã ở trang login hoặc đang redirect, không làm gì
        const currentPath = window.location.pathname;
        const isLoginPage = currentPath === '/login' || currentPath.endsWith('/login');

        if (isLoginPage) {
            console.warn('Already on login page, skipping startLogin');
            return;
        }

        // Vừa loginCallback xong (< 60s) mà lại bị gọi startLogin → đừng silent authorize
        const postLoginAt = Number(sessionStorage.getItem('oauth_post_login_at') || '0');
        if (postLoginAt && Date.now() - postLoginAt < 60000) {
            console.error('Post-login startLogin blocked — avoid authorize loop');
            sessionStorage.removeItem('oauth_post_login_at');
            this.clearTokens();
            window.location.replace(`${constants.BASE_URL}/login?error=session_invalid`);
            return;
        }

        // Chặn vòng lặp authorize trong 30s
        const loopKey = 'oauth_loop_guard';
        const last = Number(sessionStorage.getItem(loopKey) || '0');
        if (last && Date.now() - last < 30000) {
            console.error('OAuth loop detected — abort silent re-authorize');
            sessionStorage.removeItem(loopKey);
            window.location.replace(`${constants.BASE_URL}/login?error=oauth_loop`);
            return;
        }
        sessionStorage.setItem(loopKey, String(Date.now()));

        // Tránh gọi đồng thời nhiều lần
        if (this.isRedirecting) {
            console.warn('Already redirecting, skipping startLogin');
            return;
        }

        try {
            this.isRedirecting = true;
            const hasSession = await this.checkSession();
            if (hasSession) {
                this.clearSessionExpiredNotify();
                this.continueAuthorizeFlow();
            } else {
                const authorizeUrl = await this.buildAuthorizeUrl(returnUrl);
                sessionStorage.setItem('pending_authorize_url', authorizeUrl);
                window.location.href = `${constants.BASE_URL}/login`;
            }
        } finally {
            // Reset flag sau 1 giây để cho phép redirect lại nếu cần
            setTimeout(() => {
                this.isRedirecting = false;
            }, 1000);
        }
    }

    /** Đợi cookie SSO_SESSION có hiệu lực sau ssoLogin (XHR Set-Cookie). */
    async waitForSsoSession(maxAttempts = 10, delayMs = 50): Promise<boolean> {
        for (let i = 0; i < maxAttempts; i++) {
            if (await this.checkSession()) {
                return true;
            }
            await new Promise(resolve => setTimeout(resolve, delayMs));
        }
        return false;
    }

    /**
     * Kiểm tra SSO session
     */
    private async checkSession(): Promise<boolean> {
        try {
            const response = await fetch(`${AUTH_CONFIG.ssoBaseUrl}/api/auth/checkSession`, {
                method: 'GET',
                credentials: 'include', // Gửi SSO_SESSION cookie
            });

            if (!response.ok) {
                return false;
            }

            const result = await response.json();
            return result.data === true; // ApiResponse<Boolean>
        } catch (error) {
            console.error('Error checking session:', error);
            return false;
        }
    }

    async restartLogin(returnUrl: string = constants.BASE_URL || '/'): Promise<void> {
        let providerLogoutUrl: string | undefined;
        let loggedOut = false;

        try {
            const response = await fetch(AUTH_CONFIG.logoutUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });
            loggedOut = response.ok;
            if (response.ok) {
                const body = await response.json().catch(() => null);
                providerLogoutUrl = body?.data?.providerLogoutUrl || undefined;
            }
        } catch (error) {
            console.error('SSO Logout request failed:', error);
        }

        this.clearTokens();
        sessionStorage.clear();

        // Hủy session không thành công thì ép authorize bỏ qua session cũ, tránh quay lại vòng lặp
        const authorizeUrl = await this.buildAuthorizeUrl(returnUrl, { forceLogin: !loggedOut });
        sessionStorage.setItem('pending_authorize_url', authorizeUrl);

        // Phiên VNeID còn sống thì lần đăng nhập sau vẫn trả về đúng tài khoản bị chặn,
        // phải đi qua trang logout của VNeID — nơi đó redirect tiếp về trang login SSO
        window.location.href = providerLogoutUrl || `${constants.BASE_URL}/login`;
    }

    /**
     * Build authorize URL và lưu state
     */
    private async buildAuthorizeUrl(
        returnUrl: string,
        options?: { forceLogin?: boolean },
    ): Promise<string> {
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        const state = randomState();

        // Lưu state
        const authState: AuthState = { codeVerifier, state, returnUrl };
        sessionStorage.setItem(this.AUTH_STATE_KEY, JSON.stringify(authState));

        // Build URL
        const params = new URLSearchParams({
            response_type: 'code',
            client_id: AUTH_CONFIG.clientId,
            redirect_uri: AUTH_CONFIG.redirectUri,
            scope: AUTH_CONFIG.scope,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            state: state,
        });

        if (options?.forceLogin) {
            params.set('prompt', 'login');
        }

        return `${AUTH_CONFIG.authorizeUrl}?${params}`;
    }

    /**
     * Bước 2: Đăng nhập SSO (khi chưa có session)
     */
    async ssoLogin(username: string, password: string): Promise<{ mustChangePassword: boolean }> {
        try {
            // Xóa token cũ trước khi login — tránh gửi Bearer đã revoke (sau logout)
            // khiến Spring Security trả 401 trước khi xử lý username/password.
            this.clearTokens();

            let loginUrl = AUTH_CONFIG.loginUrl;
            const response = await request.post(loginUrl, { username, password }, {
                withCredentials: true,
            });

            if (response?.data && response.data.success === false) {
                throw new Error(response.data.message || 'Đăng nhập thất bại');
            }

            // Cookie SSO_SESSION vừa Set-Cookie trên XHR; đợi browser gắn cookie
            // trước khi nhảy /oauth/authorize — nếu authorize sớm hơn, filter không
            // thấy phiên và 302 về /login (form trống), lần 2 mới vào được.
            await this.waitForSsoSession();

            const payload = response?.data?.data || response?.data || {};
            return { mustChangePassword: !!payload.mustChangePassword };
        } catch (error) {
            console.error('SSO Login failed:', error);
            throw error;
        }
    }

    async startDmdcLogin(redirectAfterLogin?: string): Promise<void> {
        sessionStorage.setItem('dmdc_intent', 'login');
        const response = await request.get(AUTH_CONFIG.dmdcAuthorizeUrl, {
            withCredentials: true,
            params: redirectAfterLogin ? { redirect: redirectAfterLogin } : undefined,
        });
        const data = response.data?.data;
        if (!data?.url) {
            throw new Error('Không lấy được URL đăng nhập định danh điện tử');
        }
        if (data.state) {
            sessionStorage.setItem('dmdc_state', data.state);
        }
        window.location.href = data.url;
    }

    private parseCallbackParams(search: string): Record<string, string> {
        const result: Record<string, string> = {};
        const raw = search.startsWith('?') ? search.slice(1) : search;
        if (!raw) {
            return result;
        }
        for (const pair of raw.split('&')) {
            if (!pair) {
                continue;
            }
            const idx = pair.indexOf('=');
            const key = idx >= 0 ? pair.slice(0, idx) : pair;
            const value = idx >= 0 ? pair.slice(idx + 1) : '';
            try {
                result[decodeURIComponent(key)] = decodeURIComponent(value);
            } catch {
                result[key] = value;
            }
        }
        return result;
    }

    async handleDmdcCallback(): Promise<DmdcCallbackResult> {
        const params = this.parseCallbackParams(window.location.search);
        const code = params['code'];
        const state = params['state'];
        const encryptKey = params['encrypt_key'];
        const error = params['error'];

        if (error) {
            throw new Error(params['error_description'] || error);
        }
        if (!code) {
            throw new Error('Không nhận được mã đăng nhập từ định danh điện tử');
        }

        const savedState = sessionStorage.getItem('dmdc_state');
        if (savedState && state && savedState !== state) {
            throw new Error('State không hợp lệ - có thể bị tấn công CSRF');
        }

        const response = await request.post(
            AUTH_CONFIG.dmdcCallbackUrl,
            { code, encryptKey, state },
            { withCredentials: true },
        );

        sessionStorage.removeItem('dmdc_state');

        const data = response.data?.data ?? {};
        return {
            resultCode: data.resultCode ?? 'LOGIN_SUCCESS',
            redirectUrl: data.redirectUrl ?? null,
            linkToken: data.linkToken ?? undefined,
            linkUsername: data.linkUsername ?? undefined,
        };
    }

    async confirmDmdcLink(linkToken: string, password: string): Promise<string | null> {
        const response = await request.post(
            AUTH_CONFIG.dmdcLinkUrl,
            { linkToken, password },
            { withCredentials: true },
        );
        return response.data?.data?.redirectUrl ?? null;
    }

    async startDmdcLinkFromProfile(): Promise<void> {
        sessionStorage.setItem('dmdc_intent', 'link');
        const response = await request.get(AUTH_CONFIG.dmdcLinkAuthorizeUrl, {
            withCredentials: true,
        });
        const data = response.data?.data;
        if (!data?.url) {
            throw new Error('Không lấy được URL liên kết định danh điện tử');
        }
        if (data.state) {
            sessionStorage.setItem('dmdc_state', data.state);
        }
        window.location.href = data.url;
    }

    async resetPassword(email: string): Promise<any> {
        try {
            let resetPasswordUrl = AUTH_CONFIG.resetPassword;
            const response = await request.post(resetPasswordUrl, {email});
            return true;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Bước 3: Sau khi đăng nhập SSO thành công, tiếp tục authorize flow
     */
    continueAuthorizeFlow(): void {
        const pendingUrl = sessionStorage.getItem('pending_authorize_url');
        if (pendingUrl) {
            sessionStorage.removeItem('pending_authorize_url');
            window.location.href = pendingUrl;
        } else {
            this.buildAuthorizeUrl(this.DEFAULT_AFTER_LOGIN_URL).then(authorizeUrl => {
                window.location.href = authorizeUrl;
            });
        }
    }

    /**
     * Bước 4: Xử lý callback sau khi authorize thành công
     */
    async handleCallback(): Promise<string> {
        const params = new URLSearchParams(window.location.search);
        return this.handleCallbackWithParams(params.get('code'), params.get('state'), params.get('error'), params.get('error_description'));
    }

    async handleCallbackWithParams(
        code: string | null,
        state: string | null,
        error?: string | null,
        errorDescription?: string | null,
    ): Promise<string> {
        if (error) {
            throw new Error(errorDescription || error);
        }

        if (!code) {
            throw new Error('No authorization code received');
        }

        // Verify state
        const savedState = sessionStorage.getItem(this.AUTH_STATE_KEY);
        if (!savedState) {
            throw new Error('No auth state found');
        }

        const authState: AuthState = JSON.parse(savedState);
        if (state !== authState.state) {
            throw new Error('State mismatch - possible CSRF attack');
        }

        // Exchange code for tokens
        const tokens = await this.exchangeCodeForTokens(code, authState.codeVerifier);

        // Lưu tokens
        this.saveTokens(tokens);

        // Cleanup auth state — GIỮ oauth_loop_guard để chặn silent re-authorize
        // ngay sau callback nếu getTokens() bất ngờ coi token là hết hạn.
        sessionStorage.removeItem(this.AUTH_STATE_KEY);
        sessionStorage.setItem('oauth_post_login_at', String(Date.now()));

        return authState.returnUrl;
    }

    /**
     * Bước 5: Đổi code lấy tokens
     */
    private async exchangeCodeForTokens(
        code: string,
        codeVerifier: string
    ): Promise<TokenResponse> {
        const body = new URLSearchParams({
            grant_type: 'authorization_code',
            code: code,
            client_id: AUTH_CONFIG.clientId,
            client_secret: AUTH_CONFIG.clientSecret,
            redirect_uri: AUTH_CONFIG.redirectUri,
            code_verifier: codeVerifier,
        });

        const response = await request.post(AUTH_CONFIG.tokenUrl, body, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });

        // console.log('exchangeCodeForTokens response', response);
        if (response.status !== 200) {
            const error = response.data;
            throw new Error(
                error?.error_description || error?.error || 'Token exchange failed',
            );
        }

        return response.data;
    }

    /**
     * Refresh token khi access token hết hạn
     */
    async refreshAccessToken(): Promise<TokenResponse | null> {
        const tokens = this.getTokens();
        if (!tokens?.refresh_token) return null;

        try {
            const body = new URLSearchParams({
                grant_type: 'refresh_token',
                refresh_token: tokens.refresh_token,
                client_id: AUTH_CONFIG.clientId,
                client_secret: AUTH_CONFIG.clientSecret,
            });

            const response = await request.post(AUTH_CONFIG.tokenUrl, body,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }
                });

            if (response.status !== 200) {
                this.clearTokens();
                return null;
            }

            const newTokens = await response.data;
            this.saveTokens(newTokens);
            return newTokens;
        } catch {
            this.clearTokens();
            return null;
        }
    }

    /**
     * Lấy thông tin user
     */
    async getUserInfo(): Promise<any> {
        const tokens = this.getTokens();
        if (!tokens?.access_token) {
            throw new Error('No access token');
        }

        const response = await fetch(AUTH_CONFIG.userinfoUrl, {
            headers: {
                'Authorization': `Bearer ${tokens.access_token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                // Token expired, try refresh
                const newTokens = await this.refreshAccessToken();
                if (newTokens) {
                    return this.getUserInfo(); // Retry
                }
            }
            throw new Error('Failed to get user info');
        }

        return response.json();
    }

    // Token storage methods
    private saveTokens(tokens: TokenResponse): void {
        // Lưu mốc hết hạn theo đồng hồ trình duyệt + expires_in từ /oauth/token.
        // Không tin JWT exp tuyệt đối: nếu đồng hồ server lệch, FE sẽ xóa token ngay
        // → !isAuthenticated → startLogin silent → vòng loginCallback.
        const expiresInSec =
            typeof tokens.expires_in === 'number' && tokens.expires_in > 0
                ? tokens.expires_in
                : 900;
        const enriched = {
            ...tokens,
            client_obtained_at: Date.now(),
            client_expires_at: Date.now() + expiresInSec * 1000,
        };
        localStorage.setItem(this.TOKENS_KEY, JSON.stringify(enriched));
    }

    getTokens(): TokenResponse | null {
        const data = localStorage.getItem(this.TOKENS_KEY);
        if (!data) return null;

        try {
            const tokens = JSON.parse(data) as TokenResponse & {
                client_expires_at?: number;
            };
            if (!tokens?.access_token) {
                return null;
            }
            // Chỉ coi hết hạn theo mốc client (lúc nhận token), có leeway.
            // Không xóa token ở đây — để /api/* 401 hoặc verifySession xử lý,
            // tránh silent re-authorize loop khi JWT.exp lệch đồng hồ browser.
            if (this.isClientTokenExpired(tokens)) {
                this.flagSessionExpiredNotify();
                this.clearTokens();
                return null;
            }

            return tokens;
        } catch {
            this.clearTokens();
            return null;
        }
    }

    clearTokens(): void {
        localStorage.removeItem(this.TOKENS_KEY);
    }

    flagSessionExpiredNotify(): void {
        try {
            sessionStorage.setItem(SESSION_EXPIRED_NOTIFY_KEY, '1');
        } catch {
            //
        }
    }

    clearSessionExpiredNotify(): void {
        try {
            sessionStorage.removeItem(SESSION_EXPIRED_NOTIFY_KEY);
        } catch {
            //
        }
    }

    consumeSessionExpiredNotify(): boolean {
        try {
            const flagged = sessionStorage.getItem(SESSION_EXPIRED_NOTIFY_KEY) === '1';
            if (flagged) {
                sessionStorage.removeItem(SESSION_EXPIRED_NOTIFY_KEY);
            }
            return flagged;
        } catch {
            return false;
        }
    }

    isAuthenticated(): boolean {
        return !!this.getTokens()?.access_token;
    }

    async verifySession(): Promise<boolean> {
        const tokens = this.getTokens();
        if (!tokens?.access_token) {
            return false;
        }

        try {
            const response = await fetch(`${AUTH_CONFIG.ssoBaseUrl}/api/auth/me`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${tokens.access_token}` },
            });
            if (response.status === 401) {
                this.flagSessionExpiredNotify();
                return false;
            }
            return true;
        } catch {
            // Lỗi mạng thì giữ nguyên phiên, để interceptor xử lý ở request sau
            return true;
        }
    }

    private isClientTokenExpired(
        tokens: TokenResponse & { client_expires_at?: number },
    ): boolean {
        const leewayMs = 60_000;
        if (typeof tokens.client_expires_at === 'number') {
            return Date.now() >= tokens.client_expires_at - leewayMs;
        }
        // Token cũ (chưa có client_expires_at): fallback JWT exp + leeway lớn,
        // lỗi decode → coi như chưa hết hạn (để API quyết định).
        return this.isJwtExpired(tokens.access_token, 120);
    }

    private isJwtExpired(token: string, leewaySec: number = 60): boolean {
        try {
            const parts = token.split('.');
            if (parts.length < 2) {
                return false;
            }

            const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = payloadBase64 + '='.repeat((4 - (payloadBase64.length % 4)) % 4);
            const binary = atob(padded);
            const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
            const payload = JSON.parse(new TextDecoder().decode(bytes));

            if (!payload?.exp) {
                return false;
            }

            const now = Math.floor(Date.now() / 1000);
            return now >= Number(payload.exp) - leewaySec;
        } catch {
            return false;
        }
    }

    /** @deprecated dùng isJwtExpired / isClientTokenExpired */
    private isTokenExpired(token: string): boolean {
        return this.isJwtExpired(token, 60);
    }

    /**
     * Gọi POST /api/auth/ssoLogout (cùng origin SSO → gửi được cookie SSO_SESSION).
     * Không redirect — để trang Logout hiện kết quả rồi tự chuyển tiếp.
     */
    async performSsoLogout(redirect?: string): Promise<{
        httpStatus?: number;
        providerLogoutUrl?: string;
        postLogoutRedirectUrl?: string;
        diagnoseCode?: string;
        diagnoseMessage?: string;
        hasSsoSession?: boolean;
        vneidSession?: boolean;
        error?: string;
    }> {
        try {
            const logoutUrl = redirect
                ? `${AUTH_CONFIG.logoutUrl}?redirect=${encodeURIComponent(redirect)}`
                : AUTH_CONFIG.logoutUrl;
            const response = await fetch(logoutUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });
            const httpStatus = response.status;
            if (!response.ok) {
                this.clearTokens();
                sessionStorage.clear();
                return {
                    httpStatus,
                    error: `HTTP ${response.status}`,
                };
            }
            const body = await response.json().catch(() => null);
            const data = body?.data || {};
            this.clearTokens();
            sessionStorage.clear();
            return {
                httpStatus,
                providerLogoutUrl: data.providerLogoutUrl || undefined,
                postLogoutRedirectUrl: data.postLogoutRedirectUrl || undefined,
                diagnoseCode: data.diagnoseCode,
                diagnoseMessage: data.diagnoseMessage,
                hasSsoSession: data.hasSsoSession,
                vneidSession: data.vneidSession,
            };
        } catch (error) {
            this.clearTokens();
            sessionStorage.clear();
            return {
                error: error instanceof Error ? error.message : 'network_error',
            };
        }
    }

    async logout(redirectUri?: string, isReturn?: boolean): Promise<void> {
        const result = await this.performSsoLogout(isReturn ? redirectUri : undefined);
        const providerLogoutUrl = result.providerLogoutUrl;

        if (providerLogoutUrl) {
            if (redirectUri && isReturn) {
                this.rememberPostLogoutReturn(decodeURIComponent(redirectUri));
            }
            window.location.href = providerLogoutUrl;
        } else if (isReturn) {
            window.location.href = result.postLogoutRedirectUrl || `${constants.BASE_URL}/login`;
        } else if (redirectUri) {
            const safeRedirect = resolveSafeRedirect(redirectUri, '');
            window.location.href = safeRedirect
                ? `${constants.BASE_URL}/login?redirect=${encodeURIComponent(safeRedirect)}`
                : `${constants.BASE_URL}/login`;
        } else {
            const returnUrl = constants.BASE_URL;
            const authorizeUrl = await this.buildAuthorizeUrl(returnUrl);
            sessionStorage.setItem('pending_authorize_url', authorizeUrl);
            window.location.href = `${constants.BASE_URL}/login`;
        }
    }

    rememberPostLogoutReturn(url: string): void {
        try {
            localStorage.setItem(POST_LOGOUT_RETURN_KEY, url);
        } catch {
            // ignore
        }
    }

    /** URL app gọi logout (vd. QLST) sau khi VNeID redirect về SSO login. */
    consumePostLogoutReturn(): string | null {
        try {
            const url = localStorage.getItem(POST_LOGOUT_RETURN_KEY);
            if (url) {
                localStorage.removeItem(POST_LOGOUT_RETURN_KEY);
                return url;
            }
        } catch {
            //
        }
        return null;
    }
}

export const authService = new AuthService();
